import React, { useState } from 'react';
import { trackPixelCustomEvent, trackGAEvent } from '../../../common/analytics';
import { PlanListItemSource } from '../../../types';
import { useAppSelector } from '../../../hooks';
import { useGetCompanyToolsQuery, useAddCompanyToolMutation, useRemoveCompanyToolMutation } from '../../../services/tools';
import useAuth from '../../../components/Auth';
import { selectCompanyTools } from '../../accountSlice';
import { PlanViewInfoTabInstagramContent } from './PlanViewInfoTabInstagramContent';
import { PlanViewInfoTabYoutubeContent } from './PlanViewInfoTabYoutubeContent';
import { PlanViewInfoTabPinterestContent } from './PlanViewInfoTabPinterestContent';
import { PlanViewInfoTabTikTokContent } from './PlanViewInfoTabTikTokContent';
import { PlanDetails, QuickOrderCost } from '../types';
import { PlanViewInfoTabNativeContent } from './PlanViewInfoTabNativeContent';
import { PlanViewInfoTabYoutubeShortContent } from './PlanViewInfoTabYoutubeShortContent';
import { PlanImageDialogFullScreen } from '../../../components/PlanImageDialogFullScreen';

export const PlanViewInfoTabContent = ({plan, quickOrderCosts}: {plan: PlanDetails, quickOrderCosts: QuickOrderCost[]}) => {
	const {userProfile} = useAuth();
	const companyTools = useAppSelector(selectCompanyTools);
	useGetCompanyToolsQuery(undefined, {skip: !userProfile});
	const [addCompanyTool] = useAddCompanyToolMutation();
	const [removeCompanyTool] = useRemoveCompanyToolMutation();
	const [imageDialogOpen, setImageDialogOpen] = useState(false);
	const [selectedImageIndex, setSelectedImageIndex] = useState(0);

	const handleAddTool = (toolId: number) => {
		addCompanyTool(toolId);
	};

	const handleRemoveTool = (toolId: number) => {
		removeCompanyTool(toolId);
	};

	const handleViewCutsClick = () => {
		trackPixelCustomEvent('ViewCuts', { planId: plan?.id });
		trackGAEvent({
			category: 'UI',
			action: 'ViewCuts',
			label: 'planId',
			value: plan?.id,
		});
	};

	const handleImageClick = (index: number) => {
		setSelectedImageIndex(index);
		setImageDialogOpen(true);
	};

	const handleImageDialogClose = () => {
		setImageDialogOpen(false);
		setSelectedImageIndex(0);
	};

	if (!plan) {
		return null;
	}

	return <>
		{plan.source === PlanListItemSource.Instagram && <PlanViewInfoTabInstagramContent
			plan={plan}
			planAuthor={plan.author}
			instructionsVideos={plan.videos}
			companyTools={companyTools}
			quickOrderCosts={quickOrderCosts}
			handleViewCutsClick={handleViewCutsClick}
			handleAddTool={handleAddTool}
			handleRemoveTool={handleRemoveTool} 
			onImageClick={handleImageClick}
		/>}
		{plan.source === PlanListItemSource.Pinterest && <PlanViewInfoTabPinterestContent
			plan={plan}
			planAuthor={plan.author}
			quickOrderCosts={quickOrderCosts}
			companyTools={companyTools}
			handleViewCutsClick={handleViewCutsClick}
			handleAddTool={handleAddTool}
			handleRemoveTool={handleRemoveTool} 
		/>}
		{[PlanListItemSource.Youtube].includes(plan.source) && !!plan.description && <PlanViewInfoTabYoutubeContent
			plan={plan}
			planAuthor={plan.author}
			planImages={plan.images}
			quickOrderCosts={quickOrderCosts}
			instructionsVideos={plan.videos}
			companyTools={companyTools}
			handleViewCutsClick={handleViewCutsClick}
			handleAddTool={handleAddTool}
			handleRemoveTool={handleRemoveTool} 
			onImageClick={handleImageClick}
		/>}
		{[PlanListItemSource.Youtube].includes(plan.source) && !plan.description && <PlanViewInfoTabYoutubeShortContent
			plan={plan}
			planAuthor={plan.author}
			planImages={plan.images}
			quickOrderCosts={quickOrderCosts}
			instructionsVideos={plan.videos}
			companyTools={companyTools}
			handleViewCutsClick={handleViewCutsClick}
			handleAddTool={handleAddTool}
			handleRemoveTool={handleRemoveTool} 
		/>}
		{plan.source === PlanListItemSource.TikTok && <PlanViewInfoTabTikTokContent
			plan={plan}
			planAuthor={plan.author}
			planImages={plan.images}
			quickOrderCosts={quickOrderCosts}
			companyTools={companyTools}
			handleViewCutsClick={handleViewCutsClick}
			handleAddTool={handleAddTool}
			handleRemoveTool={handleRemoveTool}
			onImageClick={handleImageClick}
		/>}
		{plan.source === null && <PlanViewInfoTabNativeContent
			plan={plan}
			planAuthor={plan.author}
			planImages={plan.images}
			quickOrderCosts={quickOrderCosts}
			instructionsVideos={plan.videos}
			companyTools={companyTools}
			handleViewCutsClick={handleViewCutsClick}
			handleAddTool={handleAddTool}
			handleRemoveTool={handleRemoveTool}
			onImageClick={handleImageClick}
		/>}
		<PlanImageDialogFullScreen
			open={imageDialogOpen}
			onClose={handleImageDialogClose}
			images={plan.images}
			title={plan.title}
			initialIndex={selectedImageIndex}
		/>
	</>;
};

